import { useState, useEffect, useRef, createContext, useContext, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  Menu,
  Settings,
  Check,
  Home,
  MessageCircle,
  ChevronDown,
  ChevronUp,
  StickyNote,
} from 'lucide-react';
import type {
  BookData,
  ReaderLayoutProps,
  ReaderSettings,
  ReaderDataProvider,
  ReaderUser,
  Highlight,
  ReadingPosition,
} from '../types';
import ChapterNav from './ChapterNav';
import ReadingPreferences from './ReadingPreferences';
import ChapterDiscussion from './ChapterDiscussion';
import HighlightToolbar, { applyHighlightsToContent } from './HighlightToolbar';
import ChapterNotes from './ChapterNotes';
import AudioPlayer from './AudioPlayer';
import { useScrollPosition } from '../hooks/useScrollPosition';
import { useTextSelection } from '../hooks/useTextSelection';

// Context for reader state
interface ReaderContextType {
  settings: ReaderSettings;
  setSettings: (settings: ReaderSettings) => void;
  currentChapter: number;
  setCurrentChapter: (chapter: number) => void;
  bookData: BookData | null;
  markChapterComplete: (chapterId: string) => void;
}

const ReaderContext = createContext<ReaderContextType | null>(null);

export const useReader = () => {
  const context = useContext(ReaderContext);
  if (!context) throw new Error('useReader must be used within ReaderProvider');
  return context;
};

// Default settings
const defaultSettings: ReaderSettings = {
  fontSize: 18,
  lineHeight: 1.8,
  theme: 'light',
  columnWidth: 'medium',
};

export default function ReaderLayout({
  bookId,
  dataProvider,
  user,
  theme: readerTheme,
  onNavigateHome,
}: ReaderLayoutProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const articleRef = useRef<HTMLElement>(null);

  const [bookData, setBookData] = useState<BookData | null>(null);
  const [isLoadingBook, setIsLoadingBook] = useState(true);
  const [currentChapter, setCurrentChapter] = useState(0);
  const [settings, setSettings] = useState<ReaderSettings>(() => {
    try {
      const saved = localStorage.getItem('reader-settings');
      return saved ? JSON.parse(saved) : defaultSettings;
    } catch {
      return defaultSettings;
    }
  });
  const [showNav, setShowNav] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showDiscussion, setShowDiscussion] = useState(true);
  const [isChapterComplete, setIsChapterComplete] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [chapterHighlights, setChapterHighlights] = useState<Highlight[]>([]);
  const [readingPosition, setReadingPosition] = useState<ReadingPosition | null>(null);
  const [chapterAudioUrl, setChapterAudioUrl] = useState<string | null>(null);

  // Get current chapter data for queries
  const currentChapterData = bookData?.chapters[currentChapter];

  // Text selection hook for highlighting
  const { selection, clearSelection } = useTextSelection(articleRef);

  // Scroll position persistence callback
  const saveScrollPosition = useCallback(
    async (bId: string, chapter: number, position: number) => {
      await dataProvider.updateScrollPosition(bId, chapter, position);
    },
    [dataProvider]
  );

  // Scroll position hook
  const { restorePosition } = useScrollPosition({
    contentRef,
    bookId: bookData?.id ?? null,
    chapterNumber: currentChapter,
    enabled: !!user && !!bookData,
    savePosition: saveScrollPosition,
  });

  // Fetch book data
  useEffect(() => {
    let cancelled = false;
    setIsLoadingBook(true);

    dataProvider.getBook(bookId).then((data) => {
      if (cancelled) return;
      setBookData(data);
      setIsLoadingBook(false);

      // Load saved local position
      try {
        const savedPosition = localStorage.getItem(`reading-position-${bookId}`);
        if (savedPosition) {
          setCurrentChapter(parseInt(savedPosition, 10));
        }
      } catch {
        // Ignore localStorage errors
      }
    }).catch(() => {
      if (!cancelled) setIsLoadingBook(false);
    });

    return () => { cancelled = true; };
  }, [bookId, dataProvider]);

  // Fetch reading position from server
  useEffect(() => {
    if (!user || !bookData) return;
    let cancelled = false;

    dataProvider.getReadingPosition(bookData.id).then((pos) => {
      if (!cancelled) setReadingPosition(pos);
    }).catch(() => {
      // Silently fail
    });

    return () => { cancelled = true; };
  }, [user, bookData, dataProvider]);

  // Fetch highlights for current chapter
  useEffect(() => {
    if (!user || !currentChapterData) {
      setChapterHighlights([]);
      return;
    }
    let cancelled = false;

    dataProvider.getChapterHighlights(currentChapterData.id).then((highlights) => {
      if (!cancelled) setChapterHighlights(highlights);
    }).catch(() => {
      if (!cancelled) setChapterHighlights([]);
    });

    return () => { cancelled = true; };
  }, [user, currentChapterData, dataProvider]);

  // Fetch audio URL for current chapter
  useEffect(() => {
    if (!currentChapterData || !dataProvider.getChapterAudio) {
      setChapterAudioUrl(null);
      return;
    }
    let cancelled = false;

    dataProvider.getChapterAudio(currentChapterData.id).then((result) => {
      if (!cancelled && result.hasAudio && result.audioUrl) {
        setChapterAudioUrl(result.audioUrl);
      } else if (!cancelled) {
        setChapterAudioUrl(null);
      }
    }).catch(() => {
      if (!cancelled) setChapterAudioUrl(null);
    });

    return () => { cancelled = true; };
  }, [currentChapterData, dataProvider]);

  // Save settings
  useEffect(() => {
    try {
      localStorage.setItem('reader-settings', JSON.stringify(settings));
    } catch {
      // Ignore
    }
  }, [settings]);

  // Save reading position locally
  useEffect(() => {
    if (bookId) {
      try {
        localStorage.setItem(`reading-position-${bookId}`, currentChapter.toString());
      } catch {
        // Ignore
      }
    }
  }, [currentChapter, bookId]);

  // Track scroll for chapter completion
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = content;
      const scrollPercentage = (scrollTop / (scrollHeight - clientHeight)) * 100;
      if (scrollPercentage > 90) {
        setIsChapterComplete(true);
      }
    };

    content.addEventListener('scroll', handleScroll);
    return () => content.removeEventListener('scroll', handleScroll);
  }, [currentChapter]);

  // Reset chapter complete state on chapter change
  useEffect(() => {
    setIsChapterComplete(false);
    // Don't auto-scroll to top if we have a saved position to restore
    if (!readingPosition || readingPosition.currentChapter !== currentChapter) {
      contentRef.current?.scrollTo(0, 0);
    }
  }, [currentChapter, readingPosition]);

  // Restore scroll position when returning to a chapter
  useEffect(() => {
    if (readingPosition && readingPosition.currentChapter === currentChapter && readingPosition.scrollPosition > 0) {
      // Delay to allow content to render
      const timer = setTimeout(() => {
        restorePosition(readingPosition.scrollPosition);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [readingPosition, currentChapter, restorePosition]);

  // Handle creating a highlight
  const handleHighlight = async (color: string) => {
    if (!selection || !user || !currentChapterData || !bookData) return;

    try {
      await dataProvider.createHighlight({
        bookId: bookData.id,
        chapterId: currentChapterData.id,
        chapterNumber: currentChapterData.number,
        selectedText: selection.text,
        startOffset: selection.startOffset,
        endOffset: selection.endOffset,
        color,
      });
      clearSelection();
      // Refresh highlights
      const updated = await dataProvider.getChapterHighlights(currentChapterData.id);
      setChapterHighlights(updated);
    } catch (err) {
      console.error('Failed to create highlight:', err);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && currentChapter > 0) {
        setCurrentChapter(prev => prev - 1);
      }
      if (e.key === 'ArrowRight' && bookData && currentChapter < bookData.chapters.length - 1) {
        setCurrentChapter(prev => prev + 1);
      }
      if (e.key === 'Escape') {
        setShowNav(false);
        setShowSettings(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentChapter, bookData]);

  const markChapterComplete = (chapterId: string) => {
    if (!bookData) return;
    setBookData({
      ...bookData,
      chapters: bookData.chapters.map(ch =>
        ch.id === chapterId ? { ...ch, isCompleted: true } : ch
      ),
    });
  };

  const goToNextChapter = () => {
    if (bookData && currentChapter < bookData.chapters.length - 1) {
      const currentCh = bookData.chapters[currentChapter];
      markChapterComplete(currentCh.id);
      setCurrentChapter(prev => prev + 1);
    }
  };

  const goToPrevChapter = () => {
    if (currentChapter > 0) {
      setCurrentChapter(prev => prev - 1);
    }
  };

  // Loading timeout
  const [loadTimeout, setLoadTimeout] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setLoadTimeout(true), 8000);
    return () => clearTimeout(timer);
  }, []);

  if (isLoadingBook && !loadTimeout) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--off-white)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '3px solid var(--light-gray)',
            borderTopColor: 'var(--accent)',
            borderRadius: '50%',
            margin: '0 auto 1rem',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{ color: 'var(--text-light)' }}>Loading book...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Book not found
  if (!bookData) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--off-white)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: 'var(--text-light)', fontSize: '1.25rem' }}>Book not found</p>
          {onNavigateHome && (
            <button
              onClick={onNavigateHome}
              style={{
                color: 'var(--accent)',
                marginTop: '1rem',
                display: 'inline-block',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1rem',
                textDecoration: 'underline',
              }}
            >
              Return to Library
            </button>
          )}
        </div>
      </div>
    );
  }

  const chapter = bookData.chapters[currentChapter];
  const progress = ((currentChapter + 1) / bookData.chapters.length) * 100;

  // Theme styles
  const themeStyles = {
    light: { background: '#ffffff', text: 'var(--text)', secondary: 'var(--text-light)' },
    dark: { background: '#1a1a2e', text: '#e4e4e7', secondary: '#a1a1aa' },
    sepia: { background: '#f4ecd8', text: '#5c4b37', secondary: '#8b7355' },
  };

  const currentTheme = themeStyles[settings.theme];
  const columnWidths = { narrow: '580px', medium: '720px', wide: '900px' };

  return (
    <ReaderContext.Provider value={{ settings, setSettings, currentChapter, setCurrentChapter, bookData, markChapterComplete }}>
      <div style={{ minHeight: '100vh', background: currentTheme.background, transition: 'background 0.3s ease' }}>
        {/* Header */}
        <motion.header
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 50,
            background: settings.theme === 'dark' ? 'rgba(26, 26, 46, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(12px)',
            borderBottom: `1px solid ${settings.theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'var(--light-gray)'}`,
            padding: '0.75rem 1.5rem'
          }}
        >
          <div style={{
            maxWidth: '1400px',
            margin: '0 auto',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            {/* Left */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <button
                onClick={() => setShowNav(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '0.5rem',
                  cursor: 'pointer',
                  color: currentTheme.text,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.875rem'
                }}
              >
                <Menu size={20} />
                <span style={{ display: 'none' }} className="sm-show">Chapters</span>
              </button>

              {onNavigateHome && (
                <button
                  onClick={onNavigateHome}
                  style={{
                    color: currentTheme.text,
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.375rem 0.625rem',
                    borderRadius: '5px',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = settings.theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                >
                  <ChevronLeft size={18} />
                  <span>Dashboard</span>
                </button>
              )}
            </div>

            {/* Center - Title & Progress */}
            <div style={{ textAlign: 'center', flex: 1, maxWidth: '400px' }}>
              <p style={{
                fontSize: '0.875rem',
                fontWeight: 600,
                color: currentTheme.text,
                margin: '0 0 0.25rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {bookData.title}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
                <div style={{
                  flex: 1,
                  maxWidth: '120px',
                  height: '3px',
                  background: settings.theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'var(--light-gray)',
                  borderRadius: '2px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${progress}%`,
                    height: '100%',
                    background: 'var(--accent)',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                <span style={{ fontSize: '0.75rem', color: currentTheme.secondary }}>
                  {Math.round(progress)}%
                </span>
              </div>
            </div>

            {/* Right */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {/* Notes button - only show if logged in */}
              {user && (
                <button
                  onClick={() => setShowNotes(true)}
                  title="Chapter Notes"
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: '0.5rem',
                    cursor: 'pointer',
                    color: currentTheme.text,
                    display: 'flex',
                    borderRadius: '6px',
                    transition: 'background 0.15s'
                  }}
                >
                  <StickyNote size={20} />
                </button>
              )}
              <button
                onClick={() => setShowSettings(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '0.5rem',
                  cursor: 'pointer',
                  color: currentTheme.text,
                  display: 'flex'
                }}
              >
                <Settings size={20} />
              </button>
            </div>
          </div>
        </motion.header>

        {/* Main Content */}
        <main
          ref={contentRef}
          className="reader-main"
          style={{
            maxWidth: columnWidths[settings.columnWidth],
            margin: '0 auto',
            padding: '3rem 1.5rem 6rem',
            minHeight: 'calc(100vh - 60px)',
            overflowY: 'auto'
          }}
        >
          {/* Chapter Header */}
          <motion.div
            key={chapter.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
              <span style={{
                fontSize: '0.8125rem',
                color: 'var(--accent)',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.1em'
              }}>
                Chapter {chapter.number}
              </span>
              <h1 style={{
                fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
                color: currentTheme.text,
                marginTop: '0.5rem',
                fontFamily: readerTheme?.fontSerif || 'var(--font-serif)'
              }}>
                {chapter.title}
              </h1>
              <p style={{
                fontSize: '0.875rem',
                color: currentTheme.secondary,
                marginTop: '0.5rem'
              }}>
                {chapter.readingTime} read
              </p>
            </div>

            {/* Audio Player */}
            {chapterAudioUrl && (
              <AudioPlayer
                audioUrl={chapterAudioUrl}
                chapterTitle={chapter.title}
                theme={settings.theme}
              />
            )}

            {/* Chapter Content */}
            <article
              ref={articleRef}
              style={{
                fontSize: `${settings.fontSize}px`,
                lineHeight: settings.lineHeight,
                color: currentTheme.text,
                transition: 'all 0.3s ease'
              }}
              className="reader-content"
              dangerouslySetInnerHTML={{
                __html: applyHighlightsToContent(
                  formatContent(chapter.content),
                  chapterHighlights.map((h) => ({
                    id: h.id,
                    startOffset: h.startOffset,
                    endOffset: h.endOffset,
                    color: h.color,
                    note: h.note,
                  }))
                )
              }}
            />

            {/* Chapter Complete Indicator */}
            <AnimatePresence>
              {isChapterComplete && !chapter.isCompleted && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  style={{
                    textAlign: 'center',
                    padding: '2rem',
                    margin: '3rem 0',
                    background: settings.theme === 'dark' ? 'rgba(5, 150, 105, 0.1)' : 'var(--success-light)',
                    borderRadius: '1rem',
                    border: `1px solid ${settings.theme === 'dark' ? 'rgba(5, 150, 105, 0.3)' : 'var(--success)'}`
                  }}
                >
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'var(--success)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1rem'
                  }}>
                    <Check size={24} color="white" />
                  </div>
                  <p style={{ fontWeight: 600, color: 'var(--success)', margin: 0 }}>
                    Chapter Complete!
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Chapter Navigation */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '4rem',
              paddingTop: '2rem',
              borderTop: `1px solid ${settings.theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'var(--light-gray)'}`
            }}>
              <button
                onClick={goToPrevChapter}
                disabled={currentChapter === 0}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1.25rem',
                  background: 'none',
                  border: `1px solid ${settings.theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'var(--light-gray)'}`,
                  borderRadius: '0.5rem',
                  cursor: currentChapter === 0 ? 'not-allowed' : 'pointer',
                  opacity: currentChapter === 0 ? 0.5 : 1,
                  color: currentTheme.text,
                  fontSize: '0.9375rem',
                  transition: 'all 0.2s'
                }}
              >
                <ChevronLeft size={18} />
                Previous
              </button>

              <span style={{ fontSize: '0.875rem', color: currentTheme.secondary }}>
                {currentChapter + 1} of {bookData.chapters.length}
              </span>

              <button
                onClick={goToNextChapter}
                disabled={currentChapter === bookData.chapters.length - 1}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem 1.25rem',
                  background: currentChapter === bookData.chapters.length - 1 ? 'none' : 'var(--accent)',
                  border: currentChapter === bookData.chapters.length - 1
                    ? `1px solid ${settings.theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'var(--light-gray)'}`
                    : 'none',
                  borderRadius: '0.5rem',
                  cursor: currentChapter === bookData.chapters.length - 1 ? 'not-allowed' : 'pointer',
                  opacity: currentChapter === bookData.chapters.length - 1 ? 0.5 : 1,
                  color: currentChapter === bookData.chapters.length - 1 ? currentTheme.text : 'white',
                  fontSize: '0.9375rem',
                  fontWeight: 500,
                  transition: 'all 0.2s'
                }}
              >
                Next Chapter
                <ChevronRight size={18} />
              </button>
            </div>

            {/* Chapter Discussion */}
            <div style={{ marginTop: '4rem' }}>
              <button
                onClick={() => setShowDiscussion(!showDiscussion)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  padding: '1rem 1.5rem',
                  background: settings.theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'var(--off-white)',
                  border: `1px solid ${settings.theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'var(--light-gray)'}`,
                  borderRadius: '0.75rem',
                  cursor: 'pointer',
                  color: currentTheme.text
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <MessageCircle size={20} color="var(--accent)" />
                  <span style={{ fontWeight: 600 }}>Chapter Discussion</span>
                </div>
                {showDiscussion ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>

              <AnimatePresence>
                {showDiscussion && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    style={{ overflow: 'hidden' }}
                  >
                    <ChapterDiscussion
                      chapterId={chapter.id}
                      theme={settings.theme}
                      dataProvider={dataProvider}
                      user={user}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </main>

        {/* Chapter Navigation Sidebar */}
        <AnimatePresence>
          {showNav && (
            <ChapterNav
              chapters={bookData.chapters}
              currentChapter={currentChapter}
              onSelectChapter={(index) => {
                setCurrentChapter(index);
                setShowNav(false);
              }}
              onClose={() => setShowNav(false)}
              theme={settings.theme}
              materials={bookData.materials || []}
              onDownloadMaterial={(material) => {
                window.open(material.fileUrl, '_blank');
              }}
            />
          )}
        </AnimatePresence>

        {/* Settings Panel */}
        <AnimatePresence>
          {showSettings && (
            <ReadingPreferences
              settings={settings}
              onSettingsChange={setSettings}
              onClose={() => setShowSettings(false)}
            />
          )}
        </AnimatePresence>

        {/* Highlight Toolbar - shown on text selection */}
        {selection && user && (
          <HighlightToolbar
            selection={selection}
            onHighlight={handleHighlight}
            onAddNote={() => {
              handleHighlight('yellow');
              setShowNotes(true);
            }}
            onClose={clearSelection}
            theme={settings.theme}
          />
        )}

        {/* Chapter Notes Sidebar */}
        <AnimatePresence>
          {showNotes && user && currentChapterData && bookData && (
            <ChapterNotes
              bookId={bookData.id}
              chapterId={currentChapterData.id}
              chapterNumber={currentChapterData.number}
              chapterTitle={currentChapterData.title}
              onClose={() => setShowNotes(false)}
              theme={settings.theme}
              dataProvider={dataProvider}
            />
          )}
        </AnimatePresence>
      </div>
    </ReaderContext.Provider>
  );
}

// Helper function to format markdown-like content to HTML
function formatContent(content: string): string {
  return content
    .replace(/^# (.*$)/gm, '<h1 class="reader-h1">$1</h1>')
    .replace(/^## (.*$)/gm, '<h2 class="reader-h2">$1</h2>')
    .replace(/^### (.*$)/gm, '<h3 class="reader-h3">$1</h3>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/^> (.*$)/gm, '<blockquote class="reader-quote">$1</blockquote>')
    .replace(/^- (.*$)/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>)/s, '<ul class="reader-list">$1</ul>')
    .replace(/^(\d+)\. (.*$)/gm, '<li>$2</li>')
    .replace(/^---$/gm, '<hr class="reader-divider" />')
    .replace(/\n\n/g, '</p><p class="reader-paragraph">')
    .replace(/^(?!<[hublqo])/gm, '<p class="reader-paragraph">')
    .replace(/<p class="reader-paragraph"><\/p>/g, '');
}
